import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setWeirGeometry } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { ELiningThickness, ELiningThicknessImp, EStepRatio } from '../../graphicalResults/crossSection/slopedWeir/ISlopedWeir';
import { IWeirGeometryProps, IWeirGeometrySlopedWeirForm } from './IWeirGeometry';
import './WeirGeometry.scss';
import { UnitOfMeasure } from '../../../../enum/project';

const ratioOptions = [
    { value: EStepRatio.oneToOne, label: '1 / 1' },
    { value: EStepRatio.oneToTwo, label: '1 / 2' },
    { value: EStepRatio.oneToThree, label: '1 / 3' },
];

const liningThicknessOptions = [
    { value: ELiningThickness.zeroPointSeventeen, label: '0.17' },
    { value: ELiningThickness.zeroPointTwentyThree, label: '0.23' },
    { value: ELiningThickness.zeroPointThirty, label: '0.30' },
    { value: ELiningThickness.zeroPointFive, label: '0.5' },
    { value: ELiningThickness.zeroPointSix, label: '0.6' },
    { value: ELiningThickness.one, label: '1' },
];
const thicknessOptionsImp = [
    { value: ELiningThicknessImp.zeroPointFiftyFive, label: '0.55' },
    { value: ELiningThicknessImp.zeroPointSeventyFive, label: '0.75' },
    { value: ELiningThicknessImp.one, label: '1' },
    { value: ELiningThicknessImp.oneAndHalf, label: '1.5' },
    { value: ELiningThicknessImp.three, label: '3' }
];
const WeirGeometrySlopedWeir = (props: IWeirGeometryProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const defaultValue: IWeirGeometrySlopedWeirForm = {
        crestLength: (project.data!.weirGeometry as IWeirGeometrySlopedWeirForm).crestLength,
        stepRatio: (project.data!.weirGeometry as IWeirGeometrySlopedWeirForm).stepRatio,
        liningThickness: (project.data!.weirGeometry as IWeirGeometrySlopedWeirForm).liningThickness,
        height: (project.data!.weirGeometry as IWeirGeometrySlopedWeirForm).height,
    };

    const [form, setForm] = useState<IWeirGeometrySlopedWeirForm>(defaultValue);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);
        let oneToImp = isImperial ? convertTo(1, EConversionSIToImperial.m_to_ft) : 1;
        let twentyToImp = isImperial ? convertTo(20, EConversionSIToImperial.m_to_ft) : 20;

        //input validation
        if (name == 'crestLength' && value < oneToImp) {
            value = oneToImp;
            warning(t('warning_not_be_lower', { value: oneToImp }));
        } else if (name == 'height' && value > twentyToImp) {
            value = twentyToImp;
            warning(t('warning_not_be_higher', { value: twentyToImp }));
        } else if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setWeirGeometry(form as IWeirGeometrySlopedWeirForm));
        }, 1000);
    }, [form]);

    return (
        <div className='d-flex flex-column gap-2'>
            <Input
                type='number'
                title={t('input_crestLength', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.crestLength}
                name='crestLength'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='ratio'>{t('input_ratio')}</label>
                <SelectCreate
                    value={form.stepRatio}
                    name='stepRatio'
                    options={ratioOptions}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form!,
                            stepRatio: newValue,
                        })
                    }
                />
            </div>
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='liningThickness'>{t('input_liningThickness', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</label>
                <SelectCreate
                    name='liningThickness'
                    value={form.liningThickness}
                    options={isImperial ? thicknessOptionsImp : liningThicknessOptions}
                    parentOnChange={newValue =>
                        setForm({
                            ...form!,
                            liningThickness: newValue,
                        })
                    }
                />
            </div>
            <Input
                type='number'
                title={t('input_height', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.height}
                name='height'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
        </div>
    );
};

export default WeirGeometrySlopedWeir;
